import { useMemo, useState } from 'react';
import { Row, Col, Button, Form, Badge } from 'react-bootstrap';
import AdvanceTableWrapper from 'components/merlin/advance-table/AdvanceTableWrapper';
import AdvanceTable from 'components/merlin/advance-table/AdvanceTable';
import SoftBadge from 'components/common/SoftBadge';

export default function ValidationTable({ rows, onRowUpdate, onSelectionChange }) {
  const [filterMode, setFilterMode] = useState('all'); // all, valid, warnings, errors

  const columns = useMemo(() => [
    {
      accessor: 'rowNumber',
      Header: 'Row',
      Cell: ({ row }) => <span className="text-muted">{row.original.rowNumber}</span>
    },
    {
      accessor: 'status',
      Header: 'Status',
      Cell: ({ row }) => {
        const { status, errors, warnings } = row.original;
        let variant, text;
        
        if (errors && errors.length > 0) {
          variant = 'danger';
          text = 'Error';
        } else if (warnings && warnings.length > 0) {
          variant = 'warning';
          text = 'Warning';
        } else {
          variant = 'success';
          text = 'Valid';
        }

        return <SoftBadge bg={variant}>{text}</SoftBadge>;
      }
    },
    {
      accessor: 'sku',
      Header: 'SKU',
      Cell: ({ row }) => {
        const { sku, errors } = row.original;
        const skuError = errors?.find(e => e.toLowerCase().includes('sku'));
        return (
          <div>
            <span className={skuError ? 'text-danger' : ''}>{sku}</span>
            {skuError && <div className="text-danger fs--2">{skuError}</div>}
          </div>
        );
      }
    },
    {
      accessor: 'name',
      Header: 'Promotion Name',
      Cell: ({ row }) => <span>{row.original.name}</span>
    },
    {
      accessor: 'value',
      Header: 'Amount',
      Cell: ({ row }) => {
        const { value, errors } = row.original;
        const amountError = errors?.find(e => e.toLowerCase().includes('amount'));
        return (
          <div>
            <span className={amountError ? 'text-danger' : ''}>
              ${value ? Number(value).toFixed(2) : '—'}
            </span>
            {amountError && <div className="text-danger fs--2">{amountError}</div>}
          </div>
        );
      }
    },
    {
      accessor: 'startDate',
      Header: 'Start Date',
      Cell: ({ row }) => {
        const { startDate, errors } = row.original;
        const dateError = errors?.find(e => e.toLowerCase().includes('date'));
        return (
          <div>
            <span className={dateError ? 'text-danger' : ''}>
              {startDate ? startDate.toLocaleDateString() : '—'}
            </span>
          </div>
        );
      }
    },
    {
      accessor: 'endDate',
      Header: 'End Date',
      Cell: ({ row }) => {
        const { endDate, errors } = row.original;
        const dateError = errors?.find(e => e.toLowerCase().includes('date'));
        return (
          <div>
            <span className={dateError ? 'text-danger' : ''}>
              {endDate ? endDate.toLocaleDateString() : '—'}
            </span>
          </div>
        );
      }
    },
    {
      accessor: 'notes',
      Header: 'Notes',
      Cell: ({ row }) => <span className="text-muted fs--1">{row.original.notes || '—'}</span>
    },
    {
      accessor: 'messages',
      Header: 'Issues',
      Cell: ({ row }) => {
        const { errors, warnings, conflicts } = row.original;
        const allIssues = [...(errors || []), ...(warnings || []), ...(conflicts || [])];
        
        if (allIssues.length === 0) return <span className="text-success">✓</span>;
        
        return (
          <div className="fs--2">
            {allIssues.map((issue, idx) => (
              <div key={idx} className={errors?.includes(issue) ? 'text-danger' : 'text-warning'}>
                • {issue}
              </div>
            ))}
          </div>
        );
      }
    }
  ], []);

  const filteredRows = useMemo(() => {
    switch (filterMode) {
      case 'valid':
        return rows.filter(r => (!r.errors || r.errors.length === 0) && (!r.warnings || r.warnings.length === 0));
      case 'warnings':
        return rows.filter(r => r.warnings && r.warnings.length > 0);
      case 'errors':
        return rows.filter(r => r.errors && r.errors.length > 0);
      default:
        return rows;
    }
  }, [rows, filterMode]);

  const validCount = rows.filter(r => (!r.errors || r.errors.length === 0) && (!r.warnings || r.warnings.length === 0)).length;
  const warningCount = rows.filter(r => r.warnings && r.warnings.length > 0).length;
  const errorCount = rows.filter(r => r.errors && r.errors.length > 0).length;

  function BulkActions({ selectedRowIds, page }) {
    const selectedCount = Object.keys(selectedRowIds).length;

    const handleBulkAction = (action) => {
      const selectedRows = page.filter(row => selectedRowIds[row.id]);
      
      switch (action) {
        case 'remove_errors':
          // Remove all rows with errors from selection
          const idsToKeep = selectedRows.filter(r => !r.original.errors || r.original.errors.length === 0);
          break;
        case 'deselect_warnings':
          // Deselect rows with warnings
          break;
        default:
          break;
      }
    };

    return (
      <Row className="flex-between-center mb-3">
        <Col xs={12} md={6} className="d-flex align-items-center">
          <h6 className="mb-0">
            {selectedCount > 0 ? `Selected ${selectedCount} rows` : 'Preview & Review'}
          </h6>
          <div className="ms-3">
            <Badge bg="success" className="me-2">{validCount} Valid</Badge>
            <Badge bg="warning" className="me-2">{warningCount} Warnings</Badge>
            <Badge bg="danger">{errorCount} Errors</Badge>
          </div>
        </Col>
        <Col xs={12} md={6} className="text-end">
          <Form.Select 
            size="sm" 
            style={{ width: 'auto', display: 'inline-block' }} 
            className="me-2"
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}
          >
            <option value="all">Show All</option>
            <option value="valid">Valid Only</option>
            <option value="warnings">Warnings Only</option>
            <option value="errors">Errors Only</option>
          </Form.Select>
        </Col>
      </Row>
    );
  }

  return (
    <AdvanceTableWrapper
      columns={columns}
      data={filteredRows}
      sortable
      pagination
      perPage={10}
      selection
      selectionColumnWidth={30}
      onSelectionChange={onSelectionChange}
    >
      <BulkActions table />
      <AdvanceTable
        table
        headerClassName="bg-200 text-900 text-nowrap align-middle"
        rowClassName="align-middle white-space-nowrap"
        tableProps={{
          striped: true,
          className: 'fs--1 mb-0 overflow-hidden'
        }}
      />
    </AdvanceTableWrapper>
  );
}
